import { BaseDialog } from "@/lib/Components/Dialog/BaseDialog";
import { GenericForm } from "@/lib/Components/Form/Forms/GenericForm";
import { State } from "@/gql/graphql";
import { useDialogState } from "@/lib/Components/Dialog/Hooks/useDialogState";
import { DialogSubmitButton } from "@/lib/Components/Dialog/Components/DialogSubmitButton";
import { WaitlistForm } from "@/app/Waitlists/Forms/WaitlistForm";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { updateWaitlistMutation } from "@/app/Waitlists/GraphQL/waitlistMutations";
import { WaitlistListItem } from "@/app/Waitlists/GraphQL/waitlistListQuery";
import { WaitlistInput } from "@/app/Waitlists/Components/WaitlistCreateDialog";

export function WaitlistEditDialog({
  waitlist,
}: {
  waitlist: WaitlistListItem;
}) {
  const { close } = useDialogState();
  const { mutateAsync } = useGqlMutation(updateWaitlistMutation);
  return (
    <BaseDialog title={"Edit Waitlist"}>
      <GenericForm<WaitlistInput>
        initialValues={{
          earliest_departure_date: waitlist.earliest_departure_date,
          latest_departure_date: waitlist.latest_departure_date,
          departure_location: waitlist.departure_state
            ? `state:${waitlist.departure_state}`
            : `city:${waitlist.departureCity?.id}`,
          delivery_location: waitlist.delivery_state
            ? `state:${waitlist.delivery_state}`
            : `city:${waitlist.deliveryCity?.id}`,
          user_id: waitlist.user.id,
          region: waitlist.departureCity?.region ?? null!,
          vehicle_category: waitlist.vehicle_category ?? "",
          number_of_travellers: waitlist.number_of_travellers ?? null,
        }}
        onSubmit={async (values) => {
          //Parse the location to find out if it was a state or city
          const departureLocation = parseLocation(values.departure_location);
          const deliveryLocation = parseLocation(values.delivery_location);

          await mutateAsync({
            id: waitlist.id,
            input: {
              earliest_departure_date: values.earliest_departure_date,
              latest_departure_date: values.latest_departure_date,
              vehicle_category: values.vehicle_category || null,
              number_of_travellers: values.number_of_travellers,
              ...(departureLocation.type === "city"
                ? {
                    departure_city_id: departureLocation.value,
                    departure_state: null,
                  }
                : {
                    departure_state: departureLocation.value,
                    departure_city_id: null,
                  }),
              ...(deliveryLocation.type === "city"
                ? {
                    delivery_city_id: deliveryLocation.value,
                    delivery_state: null,
                  }
                : {
                    delivery_state: deliveryLocation.value,
                    delivery_city_id: null,
                  }),
            },
          });
          close();
        }}
      >
        <WaitlistForm />
        <DialogSubmitButton text={"Update"} />
      </GenericForm>
    </BaseDialog>
  );
}
type LocationValue =
  | {
      type: "state";
      value: State;
    }
  | {
      type: "city";
      value: string;
    };

function parseLocation(value: string): LocationValue {
  if (value.includes("state:")) {
    return {
      type: "state",
      value: value.substring(6) as State,
    };
  }

  if (value.includes("city:")) {
    return {
      type: "city",
      value: value.substring(5),
    };
  }

  throw new Error(`Unexpected value for location: ${value}`);
}
